import {useMemo, useEffect, useCallback, useRef} from 'react';
import type {
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum,
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
} from '@stripe-internal/data-v1/schema/types';
import {getStore, setValue} from 'src/v2/state/store';
import {useAllocateDepositAddress} from 'src/v2/components/directDeposit/useAllocateDepositAddress';
import {useUpdateRefundAddress} from 'src/v2/components/directDeposit/useUpdateRefundAddress';

type CurrencyNetwork =
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum;
type AssetCode =
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;

interface NetworkOption {
  network: CurrencyNetwork;
  isDisabled: boolean;
  compatibleCurrencies?: AssetCode[];
}

interface CurrencyOption {
  currency: AssetCode;
  isDisabled: boolean;
  compatibleNetworks?: CurrencyNetwork[];
}

interface UseDirectDepositReturn {
  selectedNetwork: CurrencyNetwork;
  setSelectedNetwork: (network: CurrencyNetwork) => void;
  selectedCurrency: AssetCode;
  setSelectedCurrency: (currency: AssetCode) => void;
  networkOptions: NetworkOption[];
  currencyOptions: CurrencyOption[];
  depositAddress: string;
  isAllocating: boolean;
  handleClose: () => void;
}

export const useDirectDeposit = (): UseDirectDepositReturn => {
  const store = getStore();
  const [updateRefundAddress] = useUpdateRefundAddress();
  const previousNetworkRef = useRef<CurrencyNetwork | null>(null);
  const hasInitializedRef = useRef(false);

  const supportedCurrencies = store.useSelector(
    (state) => state.supportedCurrencies,
  );

  const selectedNetwork = store.useSelector(
    (state) => state.directDeposit.selectedDirectDepositNetwork,
  );
  const selectedCurrency = store.useSelector(
    (state) => state.directDeposit.selectedDirectDepositCurrency,
  );

  const setSelectedNetwork = useCallback((network: CurrencyNetwork) => {
    setValue((state) => {
      state.directDeposit.selectedDirectDepositNetwork = network;
    });
  }, []);

  const setSelectedCurrency = useCallback((currency: AssetCode) => {
    setValue((state) => {
      state.directDeposit.selectedDirectDepositCurrency = currency;
    });
  }, []);

  // Get all networks with disabled state based on selected currency
  const networkOptions = useMemo(() => {
    const allNetworks = [
      ...new Set(supportedCurrencies.map((sc) => sc.currencyNetwork)),
    ];
    const compatibleNetworks = new Set(
      supportedCurrencies
        .filter((sc) => selectedCurrency && sc.assetCode === selectedCurrency)
        .map((sc) => sc.currencyNetwork),
    );

    return allNetworks.map((network) => {
      const isDisabled = !compatibleNetworks.has(network);
      const compatibleCurrencies = isDisabled
        ? [
            ...new Set(
              supportedCurrencies
                .filter((sc) => sc.currencyNetwork === network)
                .map((sc) => sc.assetCode),
            ),
          ]
        : undefined;

      return {
        network,
        isDisabled,
        compatibleCurrencies,
      };
    });
  }, [supportedCurrencies, selectedCurrency]);

  // Get all currencies with disabled state based on selected network
  const currencyOptions = useMemo(() => {
    const allCurrencies = [
      ...new Set(supportedCurrencies.map((sc) => sc.assetCode)),
    ];
    const compatibleCurrencies = new Set(
      supportedCurrencies
        .filter(
          (sc) => selectedNetwork && sc.currencyNetwork === selectedNetwork,
        )
        .map((sc) => sc.assetCode),
    );

    return allCurrencies.map((currency) => {
      const isDisabled = !compatibleCurrencies.has(currency);
      const compatibleNetworks = isDisabled
        ? [
            ...new Set(
              supportedCurrencies
                .filter((sc) => sc.assetCode === currency)
                .map((sc) => sc.currencyNetwork),
            ),
          ]
        : undefined;

      return {
        currency,
        isDisabled,
        compatibleNetworks,
      };
    });
  }, [supportedCurrencies, selectedNetwork]);

  // Auto-correct the selected network if it becomes invalid
  // This happens when the user changes currency and the previously selected
  // network isn't available for the new currency
  useEffect(() => {
    if (selectedNetwork) {
      const selectedOption = networkOptions.find(
        (opt) => opt.network === selectedNetwork,
      );
      if (selectedOption?.isDisabled) {
        const firstEnabled = networkOptions.find((opt) => !opt.isDisabled);
        if (firstEnabled) {
          setSelectedNetwork(firstEnabled.network);
        }
      }
    }
  }, [networkOptions, selectedNetwork, setSelectedNetwork]);

  // Auto-correct the selected currency if it becomes invalid
  // This happens when the user changes network and the previously selected
  // currency isn't available for the new network
  useEffect(() => {
    if (selectedCurrency) {
      const selectedOption = currencyOptions.find(
        (opt) => opt.currency === selectedCurrency,
      );
      if (selectedOption?.isDisabled) {
        const firstEnabled = currencyOptions.find((opt) => !opt.isDisabled);
        if (firstEnabled) {
          setSelectedCurrency(firstEnabled.currency);
        }
      }
    }
  }, [currencyOptions, selectedCurrency, setSelectedCurrency]);

  // Clear refund address on Direct Deposit UI load (initial mount only)
  // This handles the case where user set address on network A, then refreshed page which defaults to network B
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;

      // Clear refund address on backend to prevent network mismatch after page refresh
      updateRefundAddress({
        refundAddress: null,
        destinationNetwork: selectedNetwork,
      })
        .then(() => {
          // Clear frontend state
          setValue((state) => {
            state.directDeposit.refundMethod = 'sendToPaymentAddress';
            state.directDeposit.customRefundAddress = '';
            state.directDeposit.isRefundAddressApplied = false;
          });
        })
        .catch(() => {
          // If backend fails, still clear frontend state to prevent confusion
          setValue((state) => {
            state.directDeposit.refundMethod = 'sendToPaymentAddress';
            state.directDeposit.customRefundAddress = '';
            state.directDeposit.isRefundAddressApplied = false;
          });
        });
    }
  }, [selectedNetwork, updateRefundAddress]);

  // Auto-reset refund method when network changes (after initial mount)
  // This prevents users from having a refund address set for network A
  // when they switch to network B and make a payment
  useEffect(() => {
    const hasNetworkChanged =
      previousNetworkRef.current !== null &&
      previousNetworkRef.current !== selectedNetwork;

    // Update the ref for next comparison
    previousNetworkRef.current = selectedNetwork;

    // Always clear refund address when network changes (defensive approach)
    if (hasNetworkChanged) {
      // Clear refund address on backend first
      updateRefundAddress({
        refundAddress: null,
        destinationNetwork: selectedNetwork,
      })
        .then(() => {
          // Then clear frontend state
          setValue((state) => {
            state.directDeposit.refundMethod = 'sendToPaymentAddress';
            state.directDeposit.customRefundAddress = '';
            state.directDeposit.isRefundAddressApplied = false;
          });
        })
        .catch(() => {
          // If backend fails, still clear frontend state to prevent confusion
          setValue((state) => {
            state.directDeposit.refundMethod = 'sendToPaymentAddress';
            state.directDeposit.customRefundAddress = '';
            state.directDeposit.isRefundAddressApplied = false;
          });
        });
    }
  }, [selectedNetwork, updateRefundAddress]);

  const {address: allocatedAddress, isAllocating} = useAllocateDepositAddress({
    network: selectedNetwork,
    currency: selectedCurrency,
  });

  const selectedSupportedCurrency = useMemo(
    () =>
      supportedCurrencies.find(
        (currency) =>
          currency.currencyNetwork === selectedNetwork &&
          currency.assetCode === selectedCurrency,
      ),
    [supportedCurrencies, selectedNetwork, selectedCurrency],
  );

  const depositAddress = useMemo(() => {
    if (!selectedSupportedCurrency) {
      throw new Error(
        `No supported currency found for network: ${selectedNetwork}, currency: ${selectedCurrency}`,
      );
    }

    // Always use on-demand allocation (flag is fully rolled out)
    return allocatedAddress;
  }, [
    selectedSupportedCurrency,
    allocatedAddress,
    selectedNetwork,
    selectedCurrency,
  ]);

  const handleClose = useCallback(() => {
    setValue((state) => {
      state.screen = 'WALLET_SELECT';
    });
  }, []);

  return {
    selectedNetwork,
    setSelectedNetwork,
    selectedCurrency,
    setSelectedCurrency,
    networkOptions,
    currencyOptions,
    depositAddress,
    isAllocating,
    handleClose,
  };
};
